/**
 * draw.io / mxGraph XML → DiagramData importer (pure, unit-tested).
 *
 * Parses a practical subset of mxGraph XML:
 *   <mxGraphModel><root>
 *     <mxCell id="0"/>                         (root — skipped)
 *     <mxCell id="1" parent="0"/>              (default layer — skipped)
 *     <mxCell id="2" value="A" vertex="1" style="rounded=0;">
 *       <mxGeometry x="40" y="40" width="120" height="60" as="geometry"/>
 *     </mxCell>
 *     <mxCell id="e1" edge="1" source="2" target="3" value="link"/>
 *   </root></mxGraphModel>
 *
 * Vertex cells become DiagramData nodes (position/size/label + a shape mapped
 * from a few common mxGraph styles). Edge cells become DiagramData edges. The
 * mxGraph root (id "0") and the default layer (id "1") are skipped.
 *
 * Uses the browser DOMParser. Guards against malformed XML and always returns a
 * valid (possibly empty) DiagramData plus non-fatal warnings.
 */

import type { DiagramData, DiagramNode, DiagramEdge, NodeStyle } from '../types/schema'

type ShapeKind = NonNullable<NodeStyle['shape']>

export interface DrawioImportResult {
  data: DiagramData
  warnings: string[]
}

/**
 * Map an mxGraph `style` string to a DiagramData shape + a sensible nodeType.
 * mxGraph styles are semicolon-separated tokens; the shape is implied by tokens
 * like `ellipse`, `rhombus`, or `rounded=1`. Default is a rectangle.
 */
function shapeFromStyle(style: string): { shape: ShapeKind; nodeType: DiagramNode['nodeType'] } {
  const s = (style || '').toLowerCase()
  if (s.includes('ellipse')) return { shape: 'ellipse', nodeType: 'process' }
  if (s.includes('rhombus')) return { shape: 'diamond', nodeType: 'decision' }
  if (s.includes('cylinder')) return { shape: 'cylinder', nodeType: 'database' }
  if (s.includes('rounded=1')) return { shape: 'rounded', nodeType: 'process' }
  return { shape: 'rectangle', nodeType: 'process' }
}

/** Decode the most common XML/HTML entities draw.io emits in `value`. */
function decodeEntities(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    // Strip any remaining HTML tags draw.io may embed in rich labels.
    .replace(/<[^>]+>/g, '')
    .trim()
}

function num(value: string | null, fallback: number): number {
  if (value == null) return fallback
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Parse mxGraph XML text into DiagramData. Always returns a valid DiagramData
 * (possibly empty). Collects non-fatal warnings for skipped/odd cells.
 */
export function parseDrawioToDiagram(input: string): DrawioImportResult {
  const warnings: string[] = []
  const nodes: DiagramNode[] = []
  const edges: DiagramEdge[] = []

  if (!input || !input.trim()) {
    warnings.push('Empty input.')
    return { data: { diagramType: 'flowchart', nodes, edges }, warnings }
  }

  let doc: Document
  try {
    doc = new DOMParser().parseFromString(input, 'application/xml')
  } catch (e) {
    warnings.push('Failed to parse XML.')
    return { data: { diagramType: 'flowchart', nodes, edges }, warnings }
  }

  // DOMParser reports XML errors via a <parsererror> node rather than throwing.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    warnings.push('Malformed XML — could not parse mxGraph document.')
    return { data: { diagramType: 'flowchart', nodes, edges }, warnings }
  }

  const cells = Array.from(doc.getElementsByTagName('mxCell'))
  if (cells.length === 0) {
    warnings.push('No <mxCell> elements found.')
    return { data: { diagramType: 'flowchart', nodes, edges }, warnings }
  }

  // Track which ids became real nodes so we can validate edge endpoints.
  const nodeIds = new Set<string>()
  // Whether ANY vertex carried explicit geometry coordinates. Used to decide
  // whether to preserve imported positions or request auto-layout.
  let sawGeometry = false

  for (const cell of cells) {
    const id = cell.getAttribute('id')
    if (!id) continue
    // Skip the mxGraph root (0) and the default layer (1).
    if (id === '0' || id === '1') continue

    const isVertex = cell.getAttribute('vertex') === '1'
    const isEdge = cell.getAttribute('edge') === '1'
    const rawValue = cell.getAttribute('value') || ''
    const label = decodeEntities(rawValue)
    const style = cell.getAttribute('style') || ''

    if (isVertex) {
      const geom = cell.getElementsByTagName('mxGeometry')[0] || null
      if (geom && (geom.getAttribute('x') != null || geom.getAttribute('y') != null)) {
        sawGeometry = true
      }
      const x = num(geom?.getAttribute('x') ?? null, 0)
      const y = num(geom?.getAttribute('y') ?? null, 0)
      const width = num(geom?.getAttribute('width') ?? null, 120)
      const height = num(geom?.getAttribute('height') ?? null, 60)
      const { shape, nodeType } = shapeFromStyle(style)

      nodes.push({
        id,
        label: label || id,
        nodeType,
        position: { x, y },
        size: { width, height },
        style: { shape },
      })
      nodeIds.add(id)
      continue
    }

    if (isEdge) {
      const source = cell.getAttribute('source') || ''
      const target = cell.getAttribute('target') || ''
      if (!source || !target) {
        warnings.push(`Skipped edge "${id}" with missing source/target.`)
        continue
      }
      edges.push({
        id,
        source,
        target,
        label: label || undefined,
        type: 'custom',
      })
      continue
    }

    // Non-vertex, non-edge cell (e.g. a group/layer container) — ignore.
  }

  // Warn (non-fatally) about edges pointing at cells we didn't import as nodes.
  edges.forEach((e) => {
    if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) {
      warnings.push(`Edge "${e.id}" references an unknown node (${e.source} → ${e.target}).`)
    }
  })

  // Whether the document already carried geometry — if so, preserve the
  // imported positions; otherwise request auto-layout.
  const hasGeometry = nodes.length > 0 && sawGeometry

  const data: DiagramData = {
    diagramType: 'flowchart',
    nodes,
    edges,
    // Only request auto-layout when geometry is absent.
    ...(hasGeometry ? {} : { layout: { algorithm: 'dagre', direction: 'TB' } }),
  }

  return { data, warnings }
}
